import { format } from 'date-fns/format';
import { subDays } from 'date-fns/subDays';
import { useEffect, useMemo } from 'react';
import styled from 'styled-components';

// Shared layout, day panel and hooks for the Single and Patti calculators

// Column order follows the sheet: 1..9 then 0
export const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

// AAJ = today, KAL = yesterday, PARSO = day before yesterday
export const DAYS = [
    { key: 'PARSO', offset: 2 },
    { key: 'KAL', offset: 1 },
    { key: 'AAJ', offset: 0 },
] as const;

export type DayKey = typeof DAYS[number]['key'];

export const useDayDates = () =>
    useMemo(() => {
        const today = new Date();
        return Object.fromEntries(
            DAYS.map(d => [d.key, subDays(today, d.offset)])
        ) as Record<DayKey, Date>;
    }, []);

// Stop iOS zooming into the small inputs (zoom would make the grid scroll sideways).
// Only while the page is open; restored on leave.
export const useNoZoomViewport = () => {
    useEffect(() => {
        const meta = document.querySelector('meta[name="viewport"]');
        const original = meta?.getAttribute('content');
        meta?.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1');
        return () => {
            if (original) meta?.setAttribute('content', original);
        };
    }, []);
};

// Warn before closing/reloading the tab with unsaved numbers
export const useUnsavedWarning = (hasUnsaved: boolean) => {
    useEffect(() => {
        if (!hasUnsaved) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [hasUnsaved]);
};

export const parsePoints = (points?: string) => parseInt(points || '') || 0;

export const PageHeading = styled.h2`
  margin-top: 2rem;
  font-weight: 600;
  color: #4a90e2;
  font-size: 2rem;
  text-align: center;
  margin-bottom: 1.5rem;
  letter-spacing: 0.5px;

  @media (max-width: 576px) {
    margin-top: 1rem;
    margin-bottom: 1rem;
    font-size: 1.5rem;
  }
`;

export const Layout = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 2rem;

  @media (max-width: 992px) {
    flex-direction: column-reverse;
    gap: 12px;
  }
`;

export const SheetContainer = styled.div`
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;

  /* Stacked layout aligns to flex-start; stretch so a wide grid scrolls in its box
     instead of widening the whole page */
  @media (max-width: 992px) {
    align-self: stretch;
    width: 100%;
  }

  @media (max-width: 576px) {
    padding: 0;
    border: none;
  }
`;

// Wide grid scrolls inside its own box so the page never scrolls sideways
export const SheetScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 576px) {
    border: 1px solid #ccc;
    border-radius: 5px;
  }
`;

export const Sheet = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  min-width: 900px;
  border-top: 1px solid #ccc;
  border-left: 1px solid #ccc;

  @media (max-width: 992px) {
    min-width: 0;
  }

  /* Phones: fixed layout splits the screen width between columns, so nothing scrolls */
  @media (max-width: 576px) {
    table-layout: fixed;
  }
`;

export const Cell = styled.td`
  border-right: 1px solid #ccc;
  border-bottom: 1px solid #ccc;
  padding: 4px;
  text-align: center;
  white-space: nowrap;
  input {
    width: 100%;
    min-width: 50px;
    font-size: 16px; /* 16px stops iOS zooming on focus */
    border: none;
    background: transparent; /* lets a painted cell colour show through */
    text-align: center;
    &:focus {
      outline: none;
      background: #f0f8ff;
    }
  }

  @media (max-width: 992px) {
    input {
      min-width: 0;
    }
  }

  @media (max-width: 576px) {
    padding: 0;
    font-size: 11px;
    white-space: normal;
    overflow-wrap: anywhere;
    input {
      font-size: 12px;
      letter-spacing: -0.5px;
      padding: 6px 0;
    }
  }
`;

export const HeaderCell = styled(Cell)`
  background: #f5f5f5;
  font-weight: bold;
`;

export const TotalCell = styled(Cell)`
  font-weight: bold;
  background: #fffbe6;
`;

export const ButtonContainer = styled.div`
  margin-top: 20px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;

  @media (max-width: 576px) {
    margin-top: 12px;
    flex-direction: column-reverse;
    gap: 0;
    > * {
      width: 100%;
    }
  }
`;

const DayPanelBox = styled.div`
  width: 220px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (max-width: 992px) {
    width: 100%;
    flex-direction: row;
    gap: 8px;
  }
`;

const DayCard = styled.button<{ $active: boolean }>`
  flex: 1;
  text-align: left;
  padding: 10px 12px;
  border-radius: 5px;
  border: 2px solid ${({ $active }) => ($active ? '#4a90e2' : '#ccc')};
  background: ${({ $active }) => ($active ? '#eaf3fd' : '#fff')};
  cursor: pointer;

  .day {
    font-weight: 700;
    letter-spacing: 0.5px;
  }
  .date {
    font-size: 0.8rem;
    color: #666;
  }
  .total {
    font-size: 1.25rem;
    font-weight: 600;
    margin-top: 4px;
  }
  .unsaved {
    font-size: 0.7rem;
    font-weight: 600;
    color: #c0392b;
  }

  @media (max-width: 576px) {
    min-width: 0;
    padding: 6px 8px;
    .day {
      font-size: 0.85rem;
    }
    .date {
      font-size: 0.7rem;
    }
    .total {
      font-size: 1rem;
      overflow-wrap: anywhere;
    }
  }
`;

interface DayPanelProps {
    dayDates: Record<DayKey, Date>;
    totals: Partial<Record<DayKey, number>>;
    dirtyDays: DayKey[];
    selectedDay: DayKey;
    onSelect: (day: DayKey) => void;
}

export const DayPanel = ({ dayDates, totals, dirtyDays, selectedDay, onSelect }: DayPanelProps) => (
    <DayPanelBox>
        {DAYS.map(d => (
            <DayCard
                key={d.key}
                type="button"
                $active={selectedDay === d.key}
                onClick={() => onSelect(d.key)}
            >
                <div className="day">{d.key}</div>
                <div className="date">{format(dayDates[d.key], 'dd MMM yyyy')}</div>
                <div className="total">{totals[d.key] ?? 0}</div>
                {dirtyDays.includes(d.key) && <div className="unsaved">Unsaved</div>}
            </DayCard>
        ))}
    </DayPanelBox>
);

// ---- Cell colouring ----

// Background colours a cell can be painted with (stored as the hex value)
export const PAINT_COLORS = ['#f8b4b4', '#b7e4c7', '#fff3a3', '#b3d4fc', '#ffd8a8', '#e0c3fc'];

// cell key -> background colour
export type CellColors = Record<string, string>;

// null = normal typing, '' = eraser, otherwise the colour being painted
export type Paint = string | null;

// Painting the same colour twice clears it, so a mis-tap is easy to undo
export const applyPaint = (colors: CellColors, key: string, paint: string): CellColors => {
    const next = { ...colors };
    if (!paint || next[key] === paint) delete next[key];
    else next[key] = paint;
    return next;
};

export const parseColors = (raw: unknown): CellColors =>
    raw && typeof raw === 'object' && !Array.isArray(raw)
        ? Object.fromEntries(Object.entries(raw).filter(([, v]) => typeof v === 'string')) as CellColors
        : {};

const ColorBarBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;

  .label {
    font-weight: 600;
    margin-right: 4px;
  }
  .hint {
    font-size: 0.8rem;
    color: #666;
  }

  @media (max-width: 576px) {
    gap: 6px;
    margin-bottom: 8px;
    .label {
      display: none;
    }
    .hint {
      flex-basis: 100%;
      font-size: 0.75rem;
    }
  }
`;

const ToolButton = styled.button<{ $active: boolean; $bg?: string }>`
  min-width: 34px;
  height: 34px;
  padding: 0 10px;
  border-radius: 5px;
  border: 2px solid ${({ $active }) => ($active ? '#4a90e2' : '#ccc')};
  box-shadow: ${({ $active }) => ($active ? '0 0 0 2px #eaf3fd' : 'none')};
  background: ${({ $bg }) => $bg || '#fff'};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;

  @media (max-width: 576px) {
    min-width: 30px;
    height: 30px;
    padding: 0 8px;
    font-size: 0.8rem;
  }
`;

interface ColorBarProps {
    paint: Paint;
    onChange: (paint: Paint) => void;
}

export const ColorBar = ({ paint, onChange }: ColorBarProps) => (
    <ColorBarBox>
        <span className="label">Color:</span>
        <ToolButton type="button" $active={paint === null} onClick={() => onChange(null)}>
            Type
        </ToolButton>
        {PAINT_COLORS.map(c => (
            <ToolButton
                key={c}
                type="button"
                $active={paint === c}
                $bg={c}
                aria-label={`Paint ${c}`}
                onClick={() => onChange(c)}
            />
        ))}
        <ToolButton type="button" $active={paint === ''} onClick={() => onChange('')}>
            Erase
        </ToolButton>
        {paint !== null && <span className="hint">Tap cells to colour. Press Type to enter numbers.</span>}
    </ColorBarBox>
);

// ---- Extra boxes below the grid (free text, saved with the day) ----

export interface SheetExtras {
    aajResult: string;
    kalResult: string;
    parsoResult: string;
    todayResult: string;
    band: string;
    bandAajKhula: string;
    hisab: string;
}

export const DAY_RESULT_FIELDS = [
    { field: 'aajResult', label: 'AAJ' },
    { field: 'kalResult', label: 'KAL' },
    { field: 'parsoResult', label: 'PARSO' },
] as const;

export const EXTRA_TITLES = {
    dayResults: 'Aaj / Kal / Parso Single Result',
    todayResult: "Today's Result",
    band: 'Band',
    bandAajKhula: 'Band Aaj Khula',
    hisab: 'Hisab',
};

export const emptyExtras = (): SheetExtras => ({
    aajResult: '', kalResult: '', parsoResult: '', todayResult: '', band: '', bandAajKhula: '', hisab: '',
});

export const parseExtras = (raw: unknown): SheetExtras => {
    const extras = emptyExtras();
    if (raw && typeof raw === 'object') {
        (Object.keys(extras) as Array<keyof SheetExtras>).forEach(k => {
            const v = (raw as Record<string, unknown>)[k];
            if (typeof v === 'string') extras[k] = v;
        });
    }
    return extras;
};

const ExtrasGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 576px) {
    gap: 8px;
    margin-top: 12px;
  }
`;

// && raises specificity over ExtraBox's own label rules
const DayResults = styled.div`
  @media (max-width: 576px) {
    display: flex;
    gap: 6px;
    && label {
      flex: 1;
      min-width: 0;
      flex-direction: column;
      align-items: stretch;
      gap: 2px;
      margin-bottom: 0;
      span {
        width: auto;
        text-align: left;
      }
    }
  }
`;

// $wideOnPhone: full row on phones, normal cell elsewhere
const ExtraBox = styled.div<{ $wide?: boolean; $wideOnPhone?: boolean }>`
  grid-column: ${({ $wide }) => ($wide ? '1 / -1' : 'auto')};
  border: 1px solid #ccc;
  border-radius: 5px;
  padding: 8px 10px;
  min-width: 0;

  @media (max-width: 576px) {
    padding: 6px 8px;
    grid-column: ${({ $wide, $wideOnPhone }) => ($wide || $wideOnPhone ? '1 / -1' : 'auto')};
    .title {
      font-size: 0.8rem;
      margin-bottom: 4px;
    }
  }

  .title {
    font-weight: 700;
    font-size: 0.85rem;
    margin-bottom: 6px;
    letter-spacing: 0.3px;
  }
  label {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
    span {
      width: 52px;
      font-size: 0.8rem;
      font-weight: 600;
    }
  }
  input,
  textarea {
    width: 100%;
    min-width: 0;
    font-size: 16px; /* 16px stops iOS zooming on focus */
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 4px 6px;
    &:focus {
      outline: none;
      border-color: #4a90e2;
    }
  }
  textarea {
    resize: vertical;
  }
`;

interface ExtraBoxesProps {
    extras: SheetExtras;
    onChange: (field: keyof SheetExtras, value: string) => void;
}

export const ExtraBoxes = ({ extras, onChange }: ExtraBoxesProps) => (
    <ExtrasGrid>
        <ExtraBox $wideOnPhone>
            <div className="title">{EXTRA_TITLES.dayResults}</div>
            <DayResults>
                {DAY_RESULT_FIELDS.map(({ field, label }) => (
                    <label key={field}>
                        <span>{label}</span>
                        <input
                            type="text"
                            autoComplete="off"
                            value={extras[field]}
                            onChange={(e) => onChange(field, e.target.value)}
                        />
                    </label>
                ))}
            </DayResults>
        </ExtraBox>
        <ExtraBox $wideOnPhone>
            <div className="title">{EXTRA_TITLES.todayResult}</div>
            <input
                type="text"
                autoComplete="off"
                placeholder="123*6"
                value={extras.todayResult}
                onChange={(e) => onChange('todayResult', e.target.value)}
            />
        </ExtraBox>
        <ExtraBox>
            <div className="title">{EXTRA_TITLES.band}</div>
            <textarea rows={3} value={extras.band} onChange={(e) => onChange('band', e.target.value)} />
        </ExtraBox>
        <ExtraBox>
            <div className="title">{EXTRA_TITLES.bandAajKhula}</div>
            <textarea rows={3} value={extras.bandAajKhula} onChange={(e) => onChange('bandAajKhula', e.target.value)} />
        </ExtraBox>
        <ExtraBox $wide>
            <div className="title">{EXTRA_TITLES.hisab}</div>
            <textarea rows={5} value={extras.hisab} onChange={(e) => onChange('hisab', e.target.value)} />
        </ExtraBox>
    </ExtrasGrid>
);
